import { ChangeDetectionStrategy, Component, computed, inject, isDevMode, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ConnaissanceClientDocumentBase } from '@lib/components/investisseur-details/tabs/connaissance-client/components/connaissance-client-document-base';
import { CreateSignatureDialogComponent } from '@lib/components/signature/create-signature-dialog/create-signature-dialog.component';
import { FileDocument } from '@lib/models/file-document.model';
import { EtatRecueil, Investisseur, StorageFile } from '@lib/models/generated/graphql';
import { DocumentViewerPanelService, RecueilDocumentViewerData, RecueilTypePreference } from '@lib/services/document-viewer-panel.service';
import { getRawFileId } from '@lib/utils/app-utils';
import { OuiButtonToggleOption } from 'omnium-ui/button-toggle';
import { MenuNode } from 'omnium-ui/shared';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';
import { map } from 'rxjs/internal/operators/map';

export enum DocumentSignedType {
  signed,
  unsigned,
}

@Component({
  templateUrl: './recueil-document-viewer.component.html',
  selector: 'app-recueil-document-viewer',
  styleUrls: ['./recueil-document-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SHARED_IMPORTS],
})
export class RecueilDocumentViewerComponent extends ConnaissanceClientDocumentBase implements OnInit {
  protected override readonly documentName = "recueil d'informations";
  private viewerPanelService = inject(DocumentViewerPanelService);
  protected readonly showSendToSignature = this.viewerPanelService.showSendToSignature.asReadonly();
  readonly recueilState = toSignal(this.recueilService.recueilState$.pipe(map(state => state?.recueilEtat)), {
    initialValue: EtatRecueil.ACompleter,
  });

  readonly recueilStateConjoint = computed<EtatRecueil | undefined>(
    () => this.investisseurService.investisseurConjointSig()?.avancementConnaissanceClientInvestisseur?.recueilEtat
  );

  readonly telechargerMenuItems: MenuNode[] = [
    {
      name: 'Télécharger le document non signé',
      action: () => this.downloadDocument(DocumentSignedType.unsigned),
    },
    { name: 'Télécharger le document signé', action: () => this.downloadDocument(DocumentSignedType.signed) },
  ];
  readonly signedDocuments = signal<FileDocument[]>([]);
  readonly unsignedSplittedDocuments = signal<FileDocument[]>([]);
  readonly fileUrls = computed<string[]>(() => {
    if (this.isCouple() && this.recueilTypeSignal() === RecueilTypePreference.seule) {
      return this.unsignedSplittedDocuments()
        ?.map(d => d.url)
        .filter(Boolean) as string[];
    } else {
      return [this.unsignedDocument()?.url].filter(Boolean) as string[];
    }
  });
  readonly filenames = computed<string[]>(() => {
    if (this.isCouple() && this.recueilTypeSignal() === RecueilTypePreference.seule) {
      return this.unsignedSplittedDocuments()
        ?.map(d => d.fileName)
        .filter(Boolean) as string[];
    } else {
      return [];
    }
  });

  readonly canSendToSignature = computed<boolean>(() => {
    const state = this.recueilState();
    const canSendInv1 =
      state !== undefined &&
      ![EtatRecueil.EnSignatureApresExpiration, EtatRecueil.EnSignaturePremierFois, EtatRecueil.EnSignatureApresSigne].includes(state);

    const canSendInv2 =
      this.isCouple() &&
      this.recueilStateConjoint() !== undefined &&
      ![EtatRecueil.EnSignatureApresExpiration, EtatRecueil.EnSignaturePremierFois, EtatRecueil.EnSignatureApresSigne].includes(
        this.recueilStateConjoint() as EtatRecueil
      );
    return canSendInv1 || canSendInv2;
  });
  readonly isCouple = computed<boolean>(() => this.investisseurService.investisseurConjointSig() !== null);
  readonly recueilTypeOptions: OuiButtonToggleOption<RecueilTypePreference>[] = [
    { value: RecueilTypePreference.couple, label: 'Version couple' },
    { value: RecueilTypePreference.seule, label: 'Version seule' },
  ];

  readonly recueilTypeControl = new FormControl<RecueilTypePreference>(RecueilTypePreference.couple, { nonNullable: true });
  readonly recueilTypeSignal = signal<RecueilTypePreference>(RecueilTypePreference.couple);
  readonly documentSignedType = DocumentSignedType;
  readonly recueilType = RecueilTypePreference;

  readonly isFetchingDoc = signal<boolean>(false);

  async ngOnInit() {
    this.viewerPanelService.recueilDocumentViewerDataSubject.subscribe(async data => {
      if (data) {
        await this.loadDocuments(data);
      }
    });

    this.recueilTypeControl.valueChanges.subscribe(value => {
      this.recueilTypeSignal.set(value);
      if (value === RecueilTypePreference.seule && this.unsignedSplittedDocuments().length === 0) {
        void this.loadSplittedCoupleDocuments();
      }
      if (value === RecueilTypePreference.couple && !this.unsignedDocument()) {
        void this.loadCoupleDocument();
      }
    });
  }

  public async loadDocuments(data: RecueilDocumentViewerData): Promise<void> {
    this.isFetchingDoc.set(true);
    this.unsignedDocument.set(null);
    this.signedDocuments.set([]);
    this.unsignedSplittedDocuments.set([]);
    this.recueilTypeControl.setValue(data.recueilTypePreference, { emitEvent: false });
    this.recueilTypeSignal.set(data.recueilTypePreference);
    try {
      if (data.coupleFileMetadata) {
        const unsignedDocument = await this.recueilService.loadUnsignedDocument(data.coupleFileMetadata);
        if (unsignedDocument) {
          this.unsignedDocument.set(unsignedDocument);
        }
      }

      if (data.splittedFileMetadatas) {
        await this.loadSplittedCoupleDocuments(data.splittedFileMetadatas);
      }

      if (
        [
          EtatRecueil.Signe,
          EtatRecueil.EnSignatureApresExpiration,
          EtatRecueil.EnSignaturePremierFois,
          EtatRecueil.EnSignatureApresSigne,
        ].includes(this.recueilState() as EtatRecueil)
      ) {
        const signedDocuments: FileDocument[] = [];
        const signedDocument = await this.recueilService.loadSignedDocument();
        if (signedDocument) {
          signedDocuments.push(signedDocument);
        }
        if (this.isCouple()) {
          const recueilConjoint = await this.recueilService.loadConjointSignedDocument();
          if (recueilConjoint && getRawFileId(recueilConjoint.fileId) !== getRawFileId(signedDocument?.fileId)) {
            signedDocuments.push(recueilConjoint);
          }
        }
        this.signedDocuments.set(signedDocuments);
      }
    } catch (error) {
      if (isDevMode()) {
        console.error('error while loading document: ', error);
      }
    } finally {
      this.isFetchingDoc.set(false);
    }
  }

  private async loadCoupleDocument(): Promise<void> {
    this.isFetchingDoc.set(true);
    try {
      const unsignedDocument = await this.recueilService.loadUnsignedDocument();
      if (unsignedDocument) {
        this.unsignedDocument.set(unsignedDocument);
      }
    } finally {
      this.isFetchingDoc.set(false);
    }
  }

  public async loadSplittedCoupleDocuments(preloadedMetadata?: [StorageFile, StorageFile]): Promise<void> {
    const inv1 = this.investisseurService.investisseurSig() as Investisseur;
    const inv2 = this.investisseurService.investisseurConjointSig() as Investisseur;
    const [docInv1, docInv2] = await Promise.all([
      this.recueilService.LoadInvestisseurDocument(inv1, preloadedMetadata?.[0]),
      this.recueilService.LoadInvestisseurDocument(inv2, preloadedMetadata?.[1]),
    ]);
    if (docInv1 && docInv2) {
      this.unsignedSplittedDocuments.set([docInv1, docInv2]);
    }
  }

  public sendToSignature(): void {
    const documentsToSign =
      this.recueilTypeSignal() === RecueilTypePreference.seule ? this.unsignedSplittedDocuments() : [this.unsignedDocument()];
    const dialogRef = this.dialogService.openDialog(CreateSignatureDialogComponent, { documents: [...documentsToSign] });
    dialogRef.afterClosed().subscribe(success => {
      if (success) {
        this.investisseurService.getInvestisseur(this.investisseurService.investisseurSig()?.code as string);
      }
    });
  }

  public async downloadDocument(documentSignedType: DocumentSignedType): Promise<void> {
    let docs: FileDocument[] = [];

    if (documentSignedType === DocumentSignedType.signed) {
      docs = this.signedDocuments();
    } else {
      docs =
        !this.isCouple() || this.recueilTypeSignal() === RecueilTypePreference.couple
          ? [this.unsignedDocument()!]
          : this.unsignedSplittedDocuments();
    }

    if (!docs.length) return;

    if (docs.length === 1) {
      const { url, fileName } = docs[0];
      await this.documentService.downloadDocumentFromUrl(url as string, fileName);
      return;
    }

    await this.documentService.downloadDocumentsAsZip(
      docs.map(d => ({ url: d.url as string, name: d.fileName })),
      `recueil-informations-${documentSignedType === DocumentSignedType.signed ? 'signe' : ''}`
    );
  }

  closeSlidePanel() {
    this.viewerPanelService.closeDocumentViewer();
  }

  protected override signatureDisabledExtra(): boolean {
    return this.isDERNotReady();
  }
}
